// The whole code is copied from VPC.

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */
import { DataGrid } from '@tableau/widgets-datagrid';
import { Messages } from '../../../../gen-src/Localize';
import { L10n } from '../../../common/utils/L10n';
import { sanitize } from '../../../common/utils/Sanitize';
import { TextileHelper } from '../../../common/utils/TextileHelper';
import { IOrderClause } from '../../models/sort/IOrderClause';
import { OrderKeys } from '../../models/sort/OrderKeys';
import { ListViewStyles } from './ListViewStyles';
import { ILink, LinkCell } from './dataGridCells/LinkCell';
import { CommonColumn } from './types/Columns';
import { ContentDefinitions } from '../../models/contentDefinitions';
import { ContentType } from '../../models/ContentType';
import { ILocationInfo, LocationTypes } from '../../models/ILocationInfo';
import { SessionManagementService } from '../../services/SessionManagementService';
import { defaultHeaderFormatter } from './DefaultHeaderFormatter';
import { AppUrls } from '../../../app/AppUrls';
import { PlainTextCell } from './dataGridCells/PlainTextAndNumberCells';
import { FavoriteCell } from './dataGridCells/FavoriteCell';

export const IconColumnPreferredWidth = 44;

export type ColumnType = CommonColumn;

export interface ListViewGridColumnGeneratorParams {
  readonly displayingItems: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
  readonly displayScheduleDescriptionAsName: boolean;
  readonly inServerArea: boolean;
  readonly contentType: ContentType;
  readonly currentSort: ReadonlyArray<IOrderClause>;
  readonly changeSort: (field: string) => void;
  readonly setSortOrder: (order: ReadonlyArray<IOrderClause>) => void;
  readonly urls: AppUrls;
  readonly searchTerm: string; //used for telemetry
  readonly total: number; //used for telemetry
}

export class ListViewGridColumnGenerator {
  private params: ListViewGridColumnGeneratorParams;

  public constructor(params: ListViewGridColumnGeneratorParams) {
    this.params = params;
  }

  public updateParams(paramOverrides: Partial<ListViewGridColumnGeneratorParams>) {
    this.params = { ...this.params, ...paramOverrides };
  }

  public getColumn(columnFactoryMethod: ColumnFactoryMethod): DataGrid.Column | null {
    return columnFactoryMethod({
      generateLinkForName: this.generateLinkForName,
      generateLinkForOwner: this.generateLinkForOwner,
      generateLinkForLocation: this.generateLinkForLocation,
      listViewParams: this.params,
    });
  }

  private isAccessDenied = (row: DataGrid.Row): boolean => {
    return !!row.data.isAccessDenied;
  };

  private getContentUrl = (row: DataGrid.Row): string => {
    const contentSpaceUrlHandler = ContentDefinitions[this.params.contentType].contentSpaceUrl;
    //the collections urls use luids instead of regular ids, so we need to pass both the row key and the luid
    return contentSpaceUrlHandler ? contentSpaceUrlHandler(this.params.urls, row.key, row.data.luid) : '';
  };

  private generateLinkForLocation = (row: DataGrid.Row): ILink => {
    const userId = SessionManagementService.instance.sessionUserId();

    const location = row.data.location;
    const isOwner: boolean = row.data.owner && userId === row.data.owner.id;
    if (location) {
      return this.getLinkForLocationType(location, isOwner);
    } else if (row.data.project) {
      return {
        text: row.data.project.name,
        url: this.params.urls.projectUrl(row.data.project.id),
      };
    }

    return { text: '', url: '' };
  };

  private getLinkForLocationType = (location: ILocationInfo, isOwner: boolean): ILink => {
    switch (location.type) {
      case LocationTypes.PersonalSpace:
        return {
          text: Messages.personalSpaceGridLabel(),
          url: isOwner ? this.params.urls.personalSpaceUrl() : undefined,
          displayJustText: !isOwner,
        };
      case LocationTypes.Project:
        return {
          text: location.name,
          url: this.params.urls.projectUrl(location.id),
        };
      default:
        return {
          text: '',
          url: '',
        };
    }
  };

  // Following logic is borrowed from app/content/ProjectGrid.ts
  private generateLinkForName = (row: DataGrid.Row, testId?: string): ILink => {
    const name = row.data.name;
    const description = TextileHelper.getPlainTextFromTextile(row.data.description, sanitize);
    const title = description ? name + '\n' + description : name;

    return {
      text: name,
      column: CommonColumn.Name,
      title: this.generateTitleForName(row, title),
      url: this.getContentUrl(row),
      displayJustText: false,
      testId: testId ? testId : 'name-' + row.key,
      isAccessDenied: this.isAccessDenied(row),
    };
  };

  private generateTitleForName = (row: DataGrid.Row, title: string): string => {
    if (this.isEmbeddedDatasource(row)) {
      return Messages.datasourceEmbeddedInWorkbook_nameTooltip({
        DATASOURCE_NAME: row.data.name,
        WORKBOOK_NAME: row.data.workbook.name,
      });
    }

    return L10n.elideIfLong(title, 511); // IE only allows up to 512 characters in tooltips (unlike Firefox/Chrome)
  };

  private isEmbeddedDatasource = (row: DataGrid.Row): boolean => {
    return row.data.contentType === 'datasource' && !!row.data.workbook;
  };

  private generateLinkForOwner = (row: DataGrid.Row): ILink => {
    // detail view info's stores owner as part of workbook object
    const useWorkbookOwner = row.data.contentType === 'view';
    const owner = useWorkbookOwner && row.data.workbook ? row.data.workbook.owner : row.data.owner;

    if (!owner) {
      return { text: '', url: '' };
    }

    const displayName = owner.displayName;
    const url = this.params.urls.userUrl(owner.username, owner.domainName);

    const displayHiddenUser = owner.isHiddenInternalUser || SessionManagementService.instance.isLimitedUserVisibilityChannel();

    return {
      text: displayHiddenUser ? Messages.hiddenUser() : displayName,
      url: url,
      style: displayHiddenUser ? ListViewStyles.HiddenUserStyle : null,
      displayJustText: displayHiddenUser,
      testId: 'list-owner-cell-' + row.data.contentType + '-' + row.data.id,
      isAccessDenied: this.isAccessDenied(row),
    };
  };
}

export type ColumnFactoryMethod = (parameters: IColumnFactoryParameters) => DataGrid.Column;

export interface IColumnFactoryParameters {
  generateLinkForName: (row: DataGrid.Row, testId?: string) => ILink;
  generateLinkForOwner: (row: DataGrid.Row) => ILink;
  generateLinkForLocation: (row: DataGrid.Row) => ILink;
  listViewParams: ListViewGridColumnGeneratorParams;
}

export interface ICommonColumns {
  name: ColumnFactoryMethod;
  owner: ColumnFactoryMethod;
  location: ColumnFactoryMethod;
  description: ColumnFactoryMethod;
  favorite: ColumnFactoryMethod;
}

export const CommonColumns: ICommonColumns = {
  //The name column is the only column that has a SearchResultClicked telemetry event associated
  name: (parameters: IColumnFactoryParameters): DataGrid.Column => {
    return {
      name: CommonColumn.Name,
      dataKey: OrderKeys.name,
      sortable: false,
      headerText: Messages.column_name(),
      direction: 'asc',
      growToFit: true,
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(
        parameters.generateLinkForName,
        true,
        parameters.listViewParams.urls,
        parameters.listViewParams.searchTerm,
        parameters.listViewParams.total
      ),
    };
  },
  owner: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'owner',
      dataKey: 'ownerName',
      sortable: false,
      headerText: Messages.column_owner(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(parameters.generateLinkForOwner),
      ...{ growToFit: true },
    };
  },
  location: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'location',
      dataKey: 'containerName',
      sortable: false,
      headerText: Messages.column_location(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: LinkCell(parameters.generateLinkForLocation),
      ...{ growToFit: true },
    };
  },
  description: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'description',
      dataKey: 'description',
      sortable: false,
      headerText: Messages.column_description(),
      direction: 'asc',
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: PlainTextCell('description'),
      ...{ growToFit: true },
    };
  },
  favorite: (parameters: IColumnFactoryParameters) => {
    return {
      name: 'favorite',
      dataKey: 'favorite',
      sortable: false,
      headerText: '',
      preferredWidth: IconColumnPreferredWidth,
      headerFormatter: defaultHeaderFormatter(),
      cellFormatter: FavoriteCell(),
    };
  },
};
